import { Stack } from '@mui/material';
import { Cell } from '@telegram-apps/telegram-ui';
import { animate } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ListCategory, ListGoal } from '../../../client/types';
import { useDailyGoalProgress } from '../../../hooks/useDailyGoalProgress';
import { CustomSection } from '../../common/CustomSection';
import { GoalDisplay } from './GoalDisplay';
import { GoalDisplayPlaceholder } from './GoalDisplayPlaceholder';
import { GoalDisplaySkeleton } from './GoalDisplaySkeleton';

export type QuestHeaderSlotsProps = {
    goals: ListGoal[];
    goalCompletion: string;
    categoryMap: Record<string, ListCategory>;
    onClick: () => void;
};

export const QuestHeaderSlots = ({ goals, goalCompletion, categoryMap, onClick }: QuestHeaderSlotsProps) => {
    const actualGoals = goals ?? [];
    const slots = actualGoals.slice(0, 3);
    const emptyCount = 3 - slots.length;
    const goalSlots: (ListGoal | null)[] = [...slots, ...Array(emptyCount).fill(null)];

    const { data: dailyMap, loading } = useDailyGoalProgress(actualGoals, goalCompletion);

    const [initialized, setInitialized] = useState(false);

    const [progressMap, setProgressMap] = useState<Record<string, number>>({});
    const [xpMap, setXpMap] = useState<Record<string, number>>({});
    const [scaleMap, setScaleMap] = useState<Record<string, number>>(() =>
        Object.fromEntries(actualGoals.map(g => [g.id, 1]))
    );
    useEffect(() => {
        if (!dailyMap) return;
        if (!initialized) {
            const newProgress = Object.fromEntries(actualGoals.map(g => {
                const stats = dailyMap[g.id] ?? { xpForToday: 0, targetForToday: 0 };
                const target = stats.targetForToday;
                const xp = stats.xpForToday;
                return [g.id, target === 0 ? 0 : xp > target ? 100 : (xp / target) * 100];
            }));
            const newXpMap = Object.fromEntries(actualGoals.map(g => {
                const stats = dailyMap[g.id] ?? { xpForToday: 0, targetForToday: 0 };
                return [g.id, stats.xpForToday];
            }));
            setProgressMap(newProgress);
            setXpMap(newXpMap);
            setInitialized(true);
            return;
        }
        actualGoals.forEach(g => {
            const id = g.id;
            const stats = dailyMap[id] ?? { xpForToday: 0, targetForToday: 0 };
            const target = stats.targetForToday;
            const xpToday = stats.xpForToday;
            const newProg = target === 0 ? 0 : xpToday > target ? 100 : (xpToday / target) * 100;
            const prevProg = progressMap[id] ?? newProg;
            if (newProg !== prevProg) {
                animate(prevProg, newProg, { duration: 1, onUpdate: v => setProgressMap(pm => ({ ...pm, [id]: v })) });
            }
            const newXp = xpToday;
            const prevXp = xpMap[id] ?? newXp;
            if (newXp !== prevXp) {
                animate(prevXp, newXp, { duration: 1, onUpdate: v => setXpMap(xm => ({ ...xm, [id]: v })) });
                animate(1, 1.10, {
                    duration: 1,
                    onUpdate: v => setScaleMap(sm => ({ ...sm, [id]: v })),
                    onComplete: () => {
                        animate(1.10, 1, { duration: 0.5, onUpdate: v2 => setScaleMap(sm => ({ ...sm, [id]: v2 })) });
                    }
                });
            }
        });
    }, [actualGoals, dailyMap, initialized]);

    return (
        <Stack direction="row" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
            <CustomSection onClick={onClick}>
                <Cell style={{ '--tgui--cell--middle--padding': '6px 0', paddingLeft: '8px', paddingRight: '8px' } as any}>
                    <Stack direction="row" justifyContent="space-between" gap={2} width="100%" padding="0px" minWidth="180px">
                        {goalSlots.map((g, idx) =>
                            loading ? (
                                <GoalDisplaySkeleton key={g ? g.id : `empty-${idx}`} />
                            ) : g ? (
                                <GoalDisplay
                                    key={g.id}
                                    goal={g}
                                    categoryMap={categoryMap}
                                    progress={progressMap[g.id] ?? 0}
                                    xp={xpMap[g.id] ?? 0}
                                    target={dailyMap?.[g.id]?.targetForToday ?? 0}
                                    scale={scaleMap[g.id] ?? 1}
                                />
                            ) : (
                                <GoalDisplayPlaceholder key={`empty-${idx}`} />
                            )
                        )}
                    </Stack>
                </Cell>
            </CustomSection>
        </Stack>
    );
}; 